import { Add } from '@mui/icons-material';
import { Button, Skeleton, Typography } from '@mui/material';
import { useCommunityContext } from 'providers';
import { useMemo } from 'react';

import { LoadingOverlay } from 'shared/components/ui';
import {
  CommunityProductFragment,
  CommunitySubscriptionFragment,
  useListCommunityProductsQuery,
} from 'shared/graphql';
import { useModalState } from 'shared/hooks';

import { CommunityProductListItem } from '../../components/CommunityProductListItem';
import { CreateCommunityProductModal } from './CreateCommunityProductModal';
import { EditCommunityProductModal } from './EditCommunityProductModal';
import {
  Container,
  Content,
  Header,
  ProductsList,
  Section,
} from './SettingsCommunityProducts.styles';

const SKELETON_COUNT = 3;

export function SettingsCommunityProducts() {
  const { selectedCommunity } = useCommunityContext();

  const { data: response, loading: isLoading } = useListCommunityProductsQuery({
    skip: !selectedCommunity?.id,
    variables: { community: selectedCommunity?.id as string },
    canonizeResults: false,
  });

  const products = useMemo(() => {
    return response?.products ?? [];
  }, [response]);

  const {
    show: showEditModal,
    params: editableProduct,
    open: openEditModal,
    close: closeEditModal,
  } = useModalState<CommunityProductFragment & CommunitySubscriptionFragment>();

  const { show: showCreateModal, open: openCreateModal, close: closeCreateModal } = useModalState();

  return (
    <Container>
      <Header>
        <Typography variant="h6">Community Products / Memberships</Typography>
      </Header>

      <Content>
        <Section>
          <Typography>
            Create a product plan to allow community members to access exclusive content or
            features.
          </Typography>

          <LoadingOverlay
            loading={isLoading}
            overlay={
              <ProductsList>
                {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                  <Skeleton key={index} variant="rectangular" height={75} />
                ))}
              </ProductsList>
            }
          >
            {products.length > 0 ? (
              <ProductsList>
                {products.map(product => (
                  <CommunityProductListItem
                    key={product.id}
                    product={product}
                    onEdit={() => openEditModal(product)}
                  />
                ))}
              </ProductsList>
            ) : (
              <Typography color="textSecondary" align="center">
                No products found, create one to get started.
              </Typography>
            )}
          </LoadingOverlay>

          <Button
            startIcon={<Add />}
            variant="outlined"
            color="primary"
            disabled={isLoading}
            onClick={openCreateModal}
          >
            Add Product / Membership
          </Button>

          <CreateCommunityProductModal isOpen={showCreateModal} onClose={closeCreateModal} />

          {Boolean(editableProduct && showEditModal) && (
            <EditCommunityProductModal
              selectedProduct={editableProduct!}
              isOpen={showEditModal}
              onClose={closeEditModal}
            />
          )}
        </Section>
      </Content>
    </Container>
  );
}
